<script setup>
// "Visa ending 4242 · Your card · Change" (AppReviewIPay): the saved card a
// membership is paid or held with, same source as every other saved-card step
// (GET paymentMethods/mine, AddCardDialog to add one). Emits the chosen card id
// (null while none) so the host sends it as userPaymentMethodId.
import {computed, onMounted, ref, watch} from 'vue';
import AddCardDialog from '~/components/payments/AddCardDialog.vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {usePaymentsDisplay} from '~/composables/usePaymentsDisplay';
import {endpoints} from '~/network/endpoints';

const props = defineProps({
  /** The sentence under the row ("Nothing is taken until Jo agrees…"). */
  note: {type: String, default: null},
  disabled: {type: Boolean, default: false},
});
const emit = defineEmits(['change', 'loaded']);

const {httpRequest} = useHttpRequest();
const {cardLabel, isCardExpired} = usePaymentsDisplay();

const cards = ref(null); // null = loading
const addCardOpen = ref(false);
const chosenId = ref(null);

const usable = computed(() => (cards.value ?? []).filter((c) => !isCardExpired(c)));
const chosen = computed(() => usable.value.find((c) => c.id === chosenId.value) ?? usable.value.find((c) => c.isDefault) ?? usable.value[0] ?? null);
const hasCard = computed(() => !!chosen.value);

const load = async () => {
  try {
    cards.value = await httpRequest(endpoints.payments.myPaymentMethods, null);
  } catch {
    cards.value = [];
  }
  emit('loaded', {hasCard: hasCard.value});
};
onMounted(load);
watch(chosen, (c) => emit('change', c?.id ?? null), {immediate: true});

const onCardSaved = async () => {
  addCardOpen.value = false;
  cards.value = null;
  await load();
};
defineExpose({reload: load, hasCard});
</script>

<template>
  <div class="mscr">
    <div v-if="cards == null" class="mscr-row mscr-row--muted">
      <v-progress-circular indeterminate size="18" width="2" color="primary"/>
      <span>Loading your saved cards…</span>
    </div>
    <template v-else-if="hasCard">
      <div class="mscr-row">
        <v-icon size="18" icon="mdi-credit-card-outline"/>
        <div class="flex-fill">
          <div class="mscr-label">{{ cardLabel(chosen) }}</div>
          <div class="mscr-sub">Your card</div>
        </div>
        <v-menu v-if="usable.length > 1" location="bottom end">
          <template #activator="{props: menu}">
            <button type="button" class="mscr-link" v-bind="menu" :disabled="disabled">Change</button>
          </template>
          <v-list density="compact">
            <v-list-item v-for="c in usable" :key="c.id" :active="c.id === chosen.id" @click="chosenId = c.id">
              <v-list-item-title>{{ cardLabel(c) }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="addCardOpen = true">
              <template #prepend><v-icon size="18" icon="mdi-credit-card-plus-outline"/></template>
              <v-list-item-title>Add a card</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <button v-else type="button" class="mscr-link" :disabled="disabled" @click="addCardOpen = true">Change</button>
      </div>
    </template>
    <template v-else>
      <div class="mscr-row mscr-row--none">
        <v-icon size="18" icon="mdi-credit-card-plus-outline" color="primary"/>
        <div class="flex-fill">
          <div class="mscr-label">No card saved yet</div>
          <div class="mscr-sub">Save a card once and pay with one click, here and at every club on Tap Leagues.</div>
        </div>
        <v-btn size="small" color="primary" variant="tonal" rounded="pill" :disabled="disabled" @click="addCardOpen = true">Add a card</v-btn>
      </div>
    </template>
    <p v-if="note" class="mscr-note">{{ note }}</p>
    <add-card-dialog v-model="addCardOpen" @saved="onCardSaved"/>
  </div>
</template>

<style scoped lang="scss">
.mscr-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  font-size: 14px;

  &--muted { color: rgb(var(--v-theme-textSubtle)); font-size: 13px; }
  &--none { border-style: dashed; }
}
.mscr-label { font-weight: 600; }
.mscr-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.4; }
.mscr-link {
  border: 0;
  background: none;
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  padding: 4px;
}
.mscr-note { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; margin: 8px 2px 0; }
</style>
